import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const ThemeSwitcher: React.FC = () => {
  const { theme, setTheme, availableThemes, colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const themeLabels: Record<string, string> = {
    light: 'Light',
    dark: 'Dark',
    blue: 'Blue',
    green: 'Green',
    purple: 'Purple',
    macos: 'macOS Aqua',
  };

  const themeIcons: Record<string, React.ReactNode> = {
    light: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    dark: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    ),
    blue: (
      <div className="w-4 h-4 rounded-full bg-blue-500" />
    ),
    green: (
      <div className="w-4 h-4 rounded-full bg-green-500" />
    ),
    purple: (
      <div className="w-4 h-4 rounded-full bg-purple-500" />
    ),
    macos: (
      <div className="w-4 h-4 rounded-full bg-gradient-to-br from-slate-200 via-slate-50 to-sky-300 border border-slate-300 shadow-sm" />
    ),
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 ${colors.textMuted} hover:${colors.textSecondary} focus:outline-none focus:ring-2 focus:ring-teal-600/40 rounded-xl transition-colors`}
        title="Change theme"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute right-0 mt-2 w-48 ${colors.surface} rounded-xl shadow-lift py-1 z-50 border ${colors.border}`}>
          <div className={`px-3 py-2 border-b ${colors.border}`}>
            <span className={`text-xs font-semibold ${colors.textMuted} uppercase tracking-wide`}>Theme</span>
          </div>
          {availableThemes.map((themeOption) => (
            <button
              key={themeOption}
              onClick={() => {
                setTheme(themeOption);
                setIsOpen(false);
              }}
              className={`w-full flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                theme === themeOption
                  ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300'
                  : `${colors.textSecondary} hover:${colors.surfaceHover}`
              }`}
            >
              <span className={theme === themeOption ? 'text-teal-700 dark:text-teal-400' : colors.textMuted}>
                {themeIcons[themeOption] ?? <div className="w-4 h-4 rounded-full bg-gray-400" />}
              </span>
              <span className="flex-1 text-left">{themeLabels[themeOption]}</span>
              {theme === themeOption && (
                <svg className="w-4 h-4 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeSwitcher;

